import tinycolor from 'tinycolor2';
import { values } from 'lodash-es';
import MIKE_COLORS from '../../styles/mike-colors';
import { formatDate } from '../../helpers/date';
import { layerOrder } from '../../model/layerGroups';
import { IThreeDRenderElement } from '../../MikeVisualizer/lib/IMikeVisualizerModels';
import MikeVisualizerLib from '../../MikeVisualizer/lib/MikeVisualizer'
import { Fill, Stroke, Style, Icon, Text, Circle } from 'ol/style'; 
import { FONTFAMILY } from '../../styles/mikeSharedTheme';

/**
 * Return [r, g, b] values for the provided color.
 *
 * @param color
 */
export const _getRgbArray = (color: string): Array<number> => {
  const rgb = tinycolor(color).toRgb();
  const res = values(rgb)
    .slice(0, 3)
    .map((val) => val / 255);
  return res;
};


export const orderLayers = (layerId?: string) => {
  const moveLayerToTopLayer = (elementId: string) => {
    const { getState } = MikeVisualizerLib;
    const { renderer, renderWindow, renderedElements } = getState();
    const ids = renderedElements.map((re: IThreeDRenderElement) => re.id) 
    if (ids.includes(elementId)){
      renderer.moveEnhancedActorToTop(elementId);
      renderWindow.render();
    }  
  } 
  layerOrder.forEach((id: string) => moveLayerToTopLayer(id));  
  if (layerId){
    moveLayerToTopLayer(layerId);
  }
}

export const makeDefaultVectorStyle = ( 
  edgeColor: string = 'pink', strokeWidth = 2
) => {
  const style = new Style({   
    image: new Circle({
      fill: new Fill({
        color: edgeColor,
      }),  
      radius: 6,
    }), 
    stroke: new Stroke({
      color: edgeColor,
      width: strokeWidth,
    }),  
  });
  return style;
};

export const makePointStyle = () => {
  
    return new Style({
      image: new Icon({
        // NB: this does not seem to work with imported svgs. They need to be inlined as strings.
        src: `data:image/svg+xml;utf8,${pinSvg}`,
      }),      
    })
};

const pinSvg = `
  <svg width="17" height="24" viewBox="0 0 17 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M2.20671 8.3432C2.39563 10.86 3.30181 12.0575 4.78883 13.9397C4.80817 13.9642 4.82758 13.9888 4.84705 14.0134C5.94028 15.3969 7.24006 17.0418 8.49924 19.5663C9.75838 17.1732 10.9787 15.5771 11.9951 14.2478C12.0687 14.1516 12.1412 14.0568 12.2125 13.9632C13.6202 12.1167 14.4601 10.9007 14.6507 8.34341C14.6216 4.57499 11.6885 1.71429 8.42871 1.71429C5.16901 1.71429 2.23592 4.57489 2.20671 8.3432ZM16.4287 8.4C16.2021 11.6346 15.0251 13.1738 13.427 15.2638C12.254 16.798 10.854 18.6289 9.43647 21.6449C9.10061 22.3594 8.76376 23.1405 8.42871 24C8.1292 23.1293 7.82046 22.3389 7.50642 21.6168C6.14349 18.4824 4.68067 16.6313 3.43814 15.0589C1.86687 13.0705 0.647888 11.528 0.428711 8.4C0.428711 3.8 4.02871 0 8.42871 0C12.8287 0 16.4287 3.8 16.4287 8.4Z" fill="rgb(9,51,75)"/>
  <path d="M11.9843 7.71429C11.9843 9.60783 10.3924 11.1429 8.42871 11.1429C6.46503 11.1429 4.87316 9.60783 4.87316 7.71429C4.87316 5.82074 6.46503 4.28571 8.42871 4.28571C10.3924 4.28571 11.9843 5.82074 11.9843 7.71429Z" fill="rgb(9,51,75)"/>
  </svg>
`;
const LABELFONTSIZE = '0.8rem';
export const getPointStyle = () => {
  const pointStyle = (feature) => {    
    const text = feature && feature.id_ ? feature.id_ : "" // feature && feature.values_ && feature.values_.id ? feature.values_.id : ""
    return [new Style({
      image: new Icon({
        // NB: this does not seem to work with imported svgs. They need to be inlined as strings.
        src: `data:image/svg+xml;utf8,${pinSvg}`,
      }),      
    }),
    new Style({
      text: new Text({
        text: text,
        fill: new Fill({ color: "#09334B" }),
        font: `${LABELFONTSIZE} ${FONTFAMILY}`,
        offsetY: 16,
      }),
    })
  ]
  };
  return pointStyle;
};

export const MAP_COLORS = {
  COBALT: '#001BCB',
  BRANDBLUE_DEFAULT: MIKE_COLORS.BRANDBLUE_DEFAULT,
  YELLOW: '#FFFF00',
  APRICOT: '#F9BA32',
  LIME: '#B9D400',
  MUSTARD: '#817200',
  LEMONGREEN: '#E8FD97',
  POPPYGREEN: '#22EE5B',
  OLIVE: '#0D6D27',
  PASTELGREEN: '#B7F9A2',
  LILAC: '#AB8BE5',
  ROYALPURPLE: '#6046A4',
  LAVENDER: '#E7D4FC',
  BROWNIE: '#7D4C05',
  CHOCOLATE: '#4A1A00',
  LATTE: '#D2A16D',
  GRAPHITE: '#6D8390',
  IRON: '#424C53',
  RHINO: '#D7D7D7',
  PINK: MIKE_COLORS.PINK_DEFAULT,
  BURGUNDYPLUM: '#A9034B',
  PERSIANGREEN: '#00B283',
  TURQUOISE: '#00FFE1',
  PASTELBLUE: '#00A4EC',
  ORANGE: '#FF8A00'
};

export const MB_MAP_COLORS = {
  COBALT: _getRgbArray('#001BCB'),
  BRANDBLUE_DEFAULT: _getRgbArray(MIKE_COLORS.BRANDBLUE_DEFAULT),
  YELLOW: _getRgbArray('#FFFF00'),
  APRICOT: _getRgbArray('#F9BA32'),
  LIME: _getRgbArray('#B9D400'),
  MUSTARD: _getRgbArray('#817200'),
  LEMONGREEN: _getRgbArray('#E8FD97'),
  POPPYGREEN: _getRgbArray('#22EE5B'),
  OLIVE: _getRgbArray('#0D6D27'),
  PASTELGREEN: _getRgbArray('#B7F9A2'),
  LILAC: _getRgbArray('#AB8BE5'),
  ROYALPURPLE: _getRgbArray('#6046A4'),
  LAVENDER: _getRgbArray('#E7D4FC'),
  BROWNIE: _getRgbArray('#7D4C05'),
  CHOCOLATE: _getRgbArray('#4A1A00'),
  LATTE: _getRgbArray('#D2A16D'),
  GRAPHITE: _getRgbArray('#6D8390'),
  IRON: _getRgbArray('#424C53'),
  RHINO: _getRgbArray('#D7D7D7'),
  PINK: _getRgbArray(MIKE_COLORS.PINK_DEFAULT),
  BURGUNDYPLUM: _getRgbArray('#A9034B'),
  PERSIANGREEN: _getRgbArray('#00B283'),
  TURQUOISE: _getRgbArray('#00FFE1'),
  PASTELBLUE: _getRgbArray('#00A4EC'),
  ORANGE: _getRgbArray('#FF8A00'),
};

export const LIGHTMAP_COLORS = [
  MB_MAP_COLORS.COBALT,
  MB_MAP_COLORS.APRICOT,
  MB_MAP_COLORS.LIME,
  MB_MAP_COLORS.POPPYGREEN,
  MB_MAP_COLORS.LILAC,
  MB_MAP_COLORS.BROWNIE,
  MB_MAP_COLORS.GRAPHITE,
];

/**
 * Encodes a string to make it URL-safe.
 *
 * To be more stringent in adhering to RFC 3986 (which reserves !, ', (, ), and *), even though these characters have no formalized URI delimiting uses, the following can be safely used.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURIComponent
 *
 * @param str
 */
export function betterEncodeURIComponent(str: string): string {
  return encodeURIComponent(str).replace(/[!'()*]/g, (c) => {
    return '%' + c.charCodeAt(0).toString(16);
  });
}

type InputDate = string | Date | number | null | undefined;
/**
 * Formats a Datestring into a short format. Will not include Time
 *
 * @param inputDate
 */
export function getShortDate(inputDate: InputDate): string {
  return formatDate(inputDate);
}

/**
 * Formats a Datestring into a short format. Will include Time
 *
 * @param inputDate
 */
export function getShortDateTime(inputDate: InputDate): string {
  return formatDate(inputDate, true);
}

